import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import Toast from '../../components/ui/Toast';
import { debounce } from 'lodash';

const VAPublicProfileSettings = () => {
  const { userProfile, refreshUserProfile } = useAuth();
  const [username, setUsername] = useState(userProfile?.public_username || '');
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profileUrl, setProfileUrl] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Validate username format (letters, numbers, underscores, hyphens)
  const isValidUsername = /^[a-zA-Z0-9_-]{3,30}$/.test(username);
  
  useEffect(() => {
    if (userProfile?.public_username) {
      setProfileUrl(`${window.location.origin}/va/${userProfile.public_username}`);
    }
  }, [userProfile]);

  // Create debounced version of the check function
  const debouncedCheckUsername = debounce(async (usernameToCheck) => {
    if (!usernameToCheck || !isValidUsername) {
      setIsAvailable(false);
      setIsChecking(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('public_username', usernameToCheck)
        .neq('id', userProfile.id)
        .maybeSingle();
      
      setIsAvailable(!data);
    } catch (error) {
      console.error('Error checking username:', error);
    } finally {
      setIsChecking(false);
    }
  }, 500);

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value.toLowerCase();
    setUsername(newUsername);
    
    if (newUsername) {
      setIsChecking(true);
      setIsAvailable(false);
      debouncedCheckUsername(newUsername);
    } else {
      setIsAvailable(false);
      setIsChecking(false);
    }
  };

  const savePublicSettings = async (e) => {
    e.preventDefault();
    
    if (!username) {
      setToast({ show: true, message: 'Please enter a username for your public profile', type: 'error' });
      return;
    }
    
    if (!isValidUsername) {
      setToast({ show: true, message: 'Please enter a valid username (3-30 characters, letters, numbers, hyphens, underscores)', type: 'error' });
      return;
    }
    
    if (!isAvailable) {
      setToast({ show: true, message: 'This username is not available', type: 'error' });
      return;
    }
    
    setIsSaving(true);
    try {
      const updates = {
        is_public_profile: true,  // Always true for VA users
        public_username: username
      };
      
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userProfile.id);
      
      if (error) throw error;
      
      // Update profile URL after successful save
      setProfileUrl(`${window.location.origin}/va/${username}`);
      
      setToast({ show: true, message: 'Profile visibility settings updated', type: 'success' });
      refreshUserProfile();
    } catch (err) {
      console.error('Error updating profile visibility:', err);
      setToast({ show: true, message: err.message || 'Failed to update profile visibility', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(profileUrl)
      .then(() => setToast({ show: true, message: 'Profile link copied to clipboard', type: 'success' }))
      .catch(() => setToast({ show: true, message: 'Failed to copy link', type: 'error' }));
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
      <h2 className="text-xl font-semibold mb-4">Public Profile URL</h2>
      
      <form onSubmit={savePublicSettings}>
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-3">
            {userProfile?.public_username 
              ? "Your profile is publicly visible to help employers find you. You can customize your profile URL below."
              : "As a Virtual Assistant, your profile will be publicly visible to help employers find you. Choose your profile URL below."
            }
          </p>
          {userProfile?.public_username && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
              <p className="text-sm text-green-800">
                ✓ Your public profile is active with username: <strong>{userProfile.public_username}</strong>
              </p>
            </div>
          )}
        </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Public Username
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={username}
                onChange={handleUsernameChange}
                placeholder="your-unique-username"
                className={`px-3 py-2 border rounded-md w-full ${
                  !isAvailable && !isChecking && username ? 'border-red-500' : 'border-gray-300'
                }`}
                maxLength={30}
              />
              {isChecking && <span className="ml-2 text-gray-500">Checking...</span>}
              {!isAvailable && !isChecking && username && <span className="ml-2 text-red-500">Not available</span>}
              {isAvailable && username && !isChecking && <span className="ml-2 text-green-500">Available</span>}
            </div>
            <div className="flex flex-col mt-2">
              <p className="text-sm text-gray-500">
                Your profile will be available at: <strong>/va/{username || 'your-username'}</strong>
              </p>
              {username && !isValidUsername && (
                <p className="text-sm text-red-500 mt-1">
                  Username must be 3-30 characters and contain only letters, numbers, hyphens, or underscores.
                </p>
              )}
            </div>
          </div>
        
        <button
          type="submit"
          disabled={isSaving || !isAvailable || !isValidUsername || !username}
          className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
      
      {userProfile?.public_username && (
        <div className="mt-6 p-4 bg-gray-50 rounded-md">
          <h3 className="font-medium mb-2">Share Your Profile</h3>
          <div className="flex items-center">
            <input
              type="text"
              readOnly
              value={profileUrl}
              className="px-3 py-2 border rounded-md w-full bg-white"
            />
            <button
              onClick={copyToClipboard}
              className="ml-2 px-3 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
              type="button"
            >
              Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VAPublicProfileSettings;

